import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Plus, Users, Phone, Mail, FileText, 
  DollarSign, Eye, Edit, ChevronRight, ChevronDown, ChevronUp,
  User, Calendar, MessageSquare, Filter, X, Check, Trash2,
  AlertTriangle, RefreshCw, ExternalLink, Send, Clock,
  Instagram, Facebook, Globe, Building2, UserPlus, PhoneCall,
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icono de WhatsApp personalizado
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CO', { 
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// Colores por estado
const ESTADO_COLORS = {
  'Nuevo contacto': 'bg-blue-100 text-blue-800 border-blue-200',
  'Contactado': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'En conversación': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Pendiente de respuesta': 'bg-amber-100 text-amber-800 border-amber-200',
  'Agendó cita': 'bg-purple-100 text-purple-800 border-purple-200',
  'Cotización enviada': 'bg-orange-100 text-orange-800 border-orange-200',
  'En negociación': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Compró': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'No interesado': 'bg-zinc-100 text-zinc-800 border-zinc-200',
  'Perdido': 'bg-red-100 text-red-800 border-red-200',
  'Recontactar más adelante': 'bg-violet-100 text-violet-800 border-violet-200'
};

// Iconos por canal
const CANAL_ICONS = {
  'Instagram': Instagram,
  'Facebook': Facebook,
  'WhatsApp': () => <WhatsAppIcon className="w-4 h-4" />,
  'Página web': Globe,
  'Llamada telefónica': PhoneCall,
  'Referido': UserPlus,
  'Aliado': Building2,
  'Atención presencial': Users,
  'TikTok': () => <span className="text-xs font-bold">TT</span>,
  'Otro': MessageSquare
};

const CRMProspectos = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isGerencia } = useAuth();
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Opciones para dropdowns
  const [opciones, setOpciones] = useState({
    canales_origen: [],
    prendas_buscadas: [],
    campanas_publicidad: [],
    estados_seguimiento: [],
    asesores: []
  });
  
  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filtros, setFiltros] = useState({
    asesor_id: '',
    canal_origen: '',
    prenda_buscada: '',
    campana: '',
    estado: ''
  });
  
  // Modal crear/editar prospecto
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    documento: '',
    fecha_evento: '',
    canal_origen: 'WhatsApp',
    prenda_buscada: 'Traje de novio',
    prenda_buscada_otro: '',
    campana_publicidad: 'Orgánico',
    campana_otro: '',
    comentarios: '',
    estado_seguimiento: 'Nuevo contacto',
    asesor_inicial_id: '',
    proxima_fecha_contacto: ''
  });
  const [guardando, setGuardando] = useState(false);
  
  // Modal duplicado
  const [showDuplicadoModal, setShowDuplicadoModal] = useState(false);
  const [duplicados, setDuplicados] = useState([]);
  
  // Modal detalle/historial
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [prospectoDetalle, setProspectoDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  
  // Modal agregar contacto
  const [showContactoModal, setShowContactoModal] = useState(false);
  const [nuevoContacto, setNuevoContacto] = useState({
    canal: 'WhatsApp',
    comentario: '',
    estado_despues: '',
    proxima_accion: ''
  });

  // Estado para importación masiva
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload, preview, importing, done
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importEstadoInicial, setImportEstadoInicial] = useState('Nuevo contacto');
  const [importAccionDuplicados, setImportAccionDuplicados] = useState('agregar_historial');
  const [importResultado, setImportResultado] = useState(null);
  const fileInputRef = useRef(null);
  
  // Estado para identificar vendedor del usuario actual
  const [vendedorActual, setVendedorActual] = useState(null);

  // Cargar opciones
  const fetchOpciones = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/crm/opciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpciones(response.data);
      
      // Buscar si el usuario actual está en la lista de vendedores
      if (user && response.data.asesores) {
        const vendedorDelUsuario = response.data.asesores.find(
          v => v.usuario_id === user.id || v.correo === user.email
        );
        if (vendedorDelUsuario) {
          setVendedorActual(vendedorDelUsuario);
        }
      }
    } catch (error) {
      console.error('Error cargando opciones:', error);
    }
  }, [user]);

  // Cargar prospectos
  const fetchProspectos = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { search, page, limit: 50, ...filtros };
      // Limpiar params vacíos
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      
      const response = await axios.get(`${API_URL}/api/crm/prospectos`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectos(response.data.prospectos || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar prospectos');
    } finally {
      setLoading(false);
    }
  }, [search, page, filtros]);

  useEffect(() => {
    fetchOpciones();
  }, [fetchOpciones]);

  useEffect(() => {
    fetchProspectos();
  }, [fetchProspectos]);

  // Cuando se cargue el vendedorActual y el form está vacío, auto-asignar
  useEffect(() => {
    if (vendedorActual && !editando && !formData.asesor_inicial_id) {
      setFormData(prev => ({
        ...prev,
        asesor_inicial_id: vendedorActual.id
      }));
    }
  }, [vendedorActual, editando, formData.asesor_inicial_id]);

  // Verificar duplicados
  const verificarDuplicado = async () => {
    if (!formData.telefono && !formData.correo) return false;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/crm/prospectos/verificar-duplicado`, {
        params: { 
          telefono: formData.telefono, 
          correo: formData.correo 
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.duplicado && response.data.prospectos.length > 0) {
        setDuplicados(response.data.prospectos);
        setShowDuplicadoModal(true);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Guardar prospecto
  const handleGuardar = async (forzar = false) => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es obligatorio');
      return;
    }
    
    // Verificar duplicados si no se fuerza
    if (!forzar && !editando) {
      const hayDuplicado = await verificarDuplicado();
      if (hayDuplicado) return;
    }
    
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editando) {
        await axios.put(`${API_URL}/api/crm/prospectos/${editando.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Prospecto actualizado');
      } else {
        await axios.post(`${API_URL}/api/crm/prospectos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Prospecto creado exitosamente');
      }
      
      setShowModal(false);
      setShowDuplicadoModal(false);
      resetForm();
      fetchProspectos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Reset form
  const resetForm = () => {
    // Auto-asignar el vendedor actual si existe, de lo contrario dejar vacío
    const asesorId = vendedorActual?.id || '';
    
    setFormData({
      nombre: '',
      telefono: '',
      correo: '',
      documento: '',
      fecha_evento: '',
      canal_origen: 'WhatsApp',
      prenda_buscada: 'Traje de novio',
      prenda_buscada_otro: '',
      campana_publicidad: 'Orgánico',
      campana_otro: '',
      comentarios: '',
      estado_seguimiento: 'Nuevo contacto',
      asesor_inicial_id: asesorId,
      proxima_fecha_contacto: ''
    });
    setEditando(null);
  };

  // Abrir modal editar
  const abrirEditar = (prospecto) => {
    setEditando(prospecto);
    setFormData({
      nombre: prospecto.nombre || '',
      telefono: prospecto.telefono || '',
      correo: prospecto.correo || '',
      documento: prospecto.documento || '',
      fecha_evento: prospecto.fecha_evento || '',
      canal_origen: prospecto.canal_origen || 'WhatsApp',
      prenda_buscada: prospecto.prenda_buscada || 'Otro',
      prenda_buscada_otro: prospecto.prenda_buscada_otro || '',
      campana_publicidad: prospecto.campana_publicidad || 'Orgánico',
      campana_otro: prospecto.campana_otro || '',
      comentarios: prospecto.comentarios || '',
      estado_seguimiento: prospecto.estado_seguimiento || 'Nuevo contacto',
      asesor_inicial_id: prospecto.asesor_inicial_id || '',
      proxima_fecha_contacto: prospecto.proxima_fecha_contacto || ''
    });
    setShowModal(true);
  };

  // Ver detalle con historial
  const verDetalle = async (prospecto) => {
    setLoadingDetalle(true);
    setShowDetalleModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/crm/prospectos/${prospecto.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectoDetalle(response.data);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Contactar por WhatsApp
  // Contactar por WhatsApp
  const contactarWhatsApp = async (prospecto) => {
    if (!prospecto.telefono) {
      toast.error('Este prospecto no tiene teléfono registrado');
      return;
    }
    
    // Limpiar teléfono: solo quitar espacios, guiones, paréntesis, puntos y el símbolo +
    // NO agregar indicativos automáticamente - respetar el número como fue guardado
    let telefono = prospecto.telefono
      .replace(/[\s\-\.\(\)]/g, '')  // Quitar espacios, guiones, puntos, paréntesis
      .replace(/^\+/, '');            // Quitar el + inicial para el enlace wa.me
    
    // Advertencia si el número parece no tener indicativo (menos de 11 dígitos)
    if (telefono.length < 11) {
      console.warn(`Advertencia: El número ${telefono} podría no tener indicativo de país`);
    }
    
    // Construir mensaje
    const asesor = user?.username || 'el equipo';
    const prenda = prospecto.prenda_buscada !== 'Otro' 
      ? prospecto.prenda_buscada.toLowerCase() 
      : 'las prendas';
    
    const mensaje = encodeURIComponent(
      `Hola ${prospecto.nombre}, te saluda ${asesor} de Juan Pablo Murillo. Te escribo para continuar con la información sobre ${prenda}. ¿Cómo estás?`
    );
    
    // Registrar en historial
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/crm/prospectos/${prospecto.id}/whatsapp`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error registrando WhatsApp:', error);
    }
    
    // Abrir WhatsApp
    window.open(`https://wa.me/${telefono}?text=${mensaje}`, '_blank');
    
    // Refrescar lista
    fetchProspectos();
  };

  // Agregar nuevo contacto al historial
  const handleAgregarContacto = async () => {
    if (!prospectoDetalle) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/crm/prospectos/${prospectoDetalle.id}/contacto`,
        nuevoContacto,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Contacto agregado al historial');
      setShowContactoModal(false);
      setNuevoContacto({ canal: 'WhatsApp', comentario: '', estado_despues: '', proxima_accion: '' });
      // Refrescar detalle
      verDetalle(prospectoDetalle);
      fetchProspectos();
    } catch (error) {
      toast.error('Error al agregar contacto');
    }
  };

  // Eliminar prospecto
  const handleEliminar = async (prospecto) => {
    if (!window.confirm(`¿Eliminar a ${prospecto.nombre}? Esta acción no se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/crm/prospectos/${prospecto.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prospecto eliminado');
      fetchProspectos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Crear cotización desde prospecto
  const crearCotizacion = (prospecto) => {
    navigate('/quotes/new', { 
      state: { 
        cliente_nombre: prospecto.nombre,
        cliente_telefono: prospecto.telefono,
        cliente_correo: prospecto.correo,
        prospecto_crm_id: prospecto.id
      }
    });
  };

  // Crear ficha desde prospecto
  const crearFicha = (prospecto) => {
    navigate('/sheet/new', { 
      state: { 
        nombre: prospecto.nombre,
        telefonos: prospecto.telefono,
        correo: prospecto.correo,
        prospecto_crm_id: prospecto.id
      }
    });
  };

  // Ir a prospecto existente (desde modal duplicados)
  const irAProspectoExistente = (prospecto) => {
    setShowDuplicadoModal(false);
    setShowModal(false);
    resetForm();
    verDetalle(prospecto);
  };

  // Agregar contacto a existente
  const agregarContactoAExistente = async (prospecto) => {
    setShowDuplicadoModal(false);
    setShowModal(false);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/crm/prospectos/${prospecto.id}/contacto`,
        {
          canal: formData.canal_origen,
          comentario: `Nuevo contacto: ${formData.comentarios}`,
          estado_despues: null,
          proxima_accion: null,
          tipo_interaccion: 'registro_adicional'
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Contacto agregado al historial del cliente existente');
      resetForm();
      fetchProspectos();
    } catch (error) {
      toast.error('Error al agregar contacto');
    }
  };

  // ==================== FUNCIONES DE IMPORTACIÓN ====================
  
  // Descargar plantilla
  const descargarPlantilla = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/crm/importar/plantilla`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_importacion_clientes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };
  
  // Procesar archivo para preview
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser un Excel (.xlsx)');
      return;
    }
    
    setImportFile(file);
    setImportLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/crm/importar/preview`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportPreview(response.data);
      setImportStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
      setImportFile(null);
    } finally {
      setImportLoading(false);
    }
  };
  
  // Ejecutar importación
  const ejecutarImportacion = async () => {
    if (!importPreview?.registros) return;
    
    setImportLoading(true);
    setImportStep('importing');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/crm/importar/ejecutar`, 
        {
          registros: importPreview.registros,
          estado_inicial: importEstadoInicial,
          accion_duplicados: importAccionDuplicados
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setImportResultado(response.data);
      setImportStep('done');
      toast.success(`Importación completada: ${response.data.resumen.clientes_creados} clientes creados`);
      fetchProspectos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la importación');
      setImportStep('preview');
    } finally {
      setImportLoading(false);
    }
  };
  
  // Cerrar modal de importación y resetear
  const cerrarImportModal = () => {
    setShowImportModal(false);
    setImportStep('upload');
    setImportFile(null);
    setImportPreview(null);
    setImportResultado(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canManage = user?.role === 'gerencia' || user?.role === 'admin';

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header con Safe Area para iOS */}
      <header className="bg-black/95 backdrop-blur-xl border-b border-zinc-800 fixed top-0 left-0 w-full z-50 shadow-lg safe-area-header">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Mobile: 2 rows */}
          <div className="flex flex-col gap-3 md:hidden">
            {/* Row 1: Back + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-lg font-serif truncate">CRM / Prospectos</h1>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{total} registrados</p>
                </div>
              </div>
            </div>
            {/* Row 2: Actions */}
            <div className="flex gap-2">
              {canManage && (
                <button
                  onClick={() => { setShowImportModal(true); setImportStep('upload'); }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors"
                  data-testid="importar-excel-btn-mobile"
                >
                  <Upload className="w-4 h-4" />
                  Importar
                </button>
              )}
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black text-sm font-medium rounded-xl transition-colors"
                data-testid="nuevo-prospecto-btn-mobile"
              >
                <Plus className="w-4 h-4" />
                Nuevo
              </button>
            </div>
          </div>
          
          {/* Desktop: Single row */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-xl font-serif">CRM / Gestión de Prospectos</h1>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">{total} {total === 1 ? 'prospecto registrado' : 'prospectos registrados'}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Botón Importar Excel - Solo para admin/gerencia */}
              {canManage && (
                <button
                  onClick={() => { setShowImportModal(true); setImportStep('upload'); }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors shadow-sm"
                  data-testid="importar-excel-btn"
                >
                  <Upload className="w-4 h-4" />
                  <span>Importar Excel</span>
                </button>
              )}
              
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium rounded-xl transition-colors shadow-sm"
                data-testid="nuevo-prospecto-btn"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Prospecto</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 py-6 content-below-safe-header">
        {/* Barra de búsqueda y filtros */}
        <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-emerald-500 rounded-xl p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <form onSubmit={(e) => { e.preventDefault(); fetchProspectos(); }} className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, teléfono, correo..."
                  className="w-full pl-12 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                  data-testid="search-crm"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-700 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                )}
              </div>
            </form>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-colors ${
                showFilters ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
              }`}
              data-testid="toggle-filters"
            >
              <Filter className="w-4 h-4" />
              Filtros
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            <button
              onClick={fetchProspectos}
              className="p-3 bg-zinc-800 border border-zinc-700 rounded-xl hover:border-zinc-600 transition-colors"
              title="Actualizar"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Asesor</label>
              <select
                value={filtros.asesor_id}
                onChange={(e) => setFiltros({...filtros, asesor_id: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Todos</option>
                {opciones.asesores.map(a => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Canal</label>
              <select
                value={filtros.canal_origen}
                onChange={(e) => setFiltros({...filtros, canal_origen: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Todos</option>
                {opciones.canales_origen.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Prenda</label>
              <select
                value={filtros.prenda_buscada}
                onChange={(e) => setFiltros({...filtros, prenda_buscada: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Todas</option>
                {opciones.prendas_buscadas.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Campaña</label>
              <select
                value={filtros.campana}
                onChange={(e) => setFiltros({...filtros, campana: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Todas</option>
                {opciones.campanas_publicidad.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Todos</option>
                {opciones.estados_seguimiento.map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabla de prospectos */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-700 sticky top-0">
                <tr className="text-left text-xs text-zinc-100 uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Asesor</th>
                  <th className="px-4 py-3 font-semibold">F. Evento</th>
                  <th className="px-4 py-3 font-semibold">Nombre</th>
                  <th className="px-4 py-3 font-semibold">Canal</th>
                  <th className="px-4 py-3 font-semibold">Teléfono</th>
                  <th className="px-4 py-3 font-semibold">Correo</th>
                  <th className="px-4 py-3 font-semibold">Prenda</th>
                  <th className="px-4 py-3 font-semibold">Campaña</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-center"># Cont.</th>
                  <th className="px-4 py-3 font-semibold">Último</th>
                  <th className="px-4 py-3 font-semibold text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#D4AF37]" />
                    </td>
                  </tr>
                ) : prospectos.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-12 text-center text-zinc-500">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay prospectos registrados</p>
                      <button
                        onClick={() => { resetForm(); setShowModal(true); }}
                        className="mt-3 text-[#D4AF37] hover:underline"
                      >
                        Registrar primer prospecto
                      </button>
                    </td>
                  </tr>
                ) : (
                  prospectos.map((prospecto) => {
                    const CanalIcon = CANAL_ICONS[prospecto.canal_origen] || MessageSquare;
                    
                    return (
                      <tr 
                        key={prospecto.id} 
                        className="hover:bg-zinc-800/50 transition-colors"
                      >
                        {/* Asesor */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-300 text-xs">
                            {prospecto.asesor_inicial_nombre || '-'}
                          </span>
                        </td>
                        
                        {/* Fecha Evento */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-400 text-xs">
                            {prospecto.fecha_evento ? formatDate(prospecto.fecha_evento) : '-'}
                          </span>
                        </td>
                        
                        {/* Nombre */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => verDetalle(prospecto)}
                            className="font-medium text-white hover:text-[#D4AF37] transition-colors text-left"
                          >
                            {prospecto.nombre}
                          </button>
                        </td>
                        
                        {/* Canal */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <CanalIcon className="w-4 h-4 text-zinc-400" />
                            <span className="text-xs text-zinc-400">{prospecto.canal_origen}</span>
                          </div>
                        </td>
                        
                        {/* Teléfono */}
                        <td className="px-4 py-3">
                          {prospecto.telefono ? (
                            <button
                              onClick={() => contactarWhatsApp(prospecto)}
                              className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors"
                              title="Contactar por WhatsApp"
                            >
                              <WhatsAppIcon className="w-4 h-4" />
                              <span className="text-xs">{prospecto.telefono}</span>
                            </button>
                          ) : (
                            <span className="text-zinc-500 text-xs">-</span>
                          )}
                        </td>
                        
                        {/* Correo */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-400 text-xs truncate max-w-[150px] block">
                            {prospecto.correo || '-'}
                          </span>
                        </td>
                        
                        {/* Prenda */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-300 text-xs">
                            {prospecto.prenda_buscada === 'Otro' 
                              ? prospecto.prenda_buscada_otro || 'Otro' 
                              : prospecto.prenda_buscada}
                          </span>
                        </td>
                        
                        {/* Campaña */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-400 text-xs">
                            {prospecto.campana_publicidad === 'Otro'
                              ? prospecto.campana_otro || 'Otro'
                              : prospecto.campana_publicidad}
                          </span>
                        </td>
                        
                        {/* Estado */}
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                            ESTADO_COLORS[prospecto.estado_seguimiento] || 'bg-zinc-100 text-zinc-800'
                          }`}>
                            {prospecto.estado_seguimiento}
                          </span>
                        </td>
                        
                        {/* # Contactos */}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            prospecto.numero_contactos > 3 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-zinc-700 text-zinc-300'
                          }`}>
                            {prospecto.numero_contactos || 1}
                          </span>
                        </td>
                        
                        {/* Último contacto */}
                        <td className="px-4 py-3">
                          <span className="text-zinc-500 text-xs">
                            {prospecto.ultimo_contacto 
                              ? formatDate(prospecto.ultimo_contacto) 
                              : formatDate(prospecto.created_at)}
                          </span>
                        </td>
                        
                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => contactarWhatsApp(prospecto)}
                              className="p-1.5 hover:bg-emerald-500/20 rounded-lg transition-colors"
                              title="WhatsApp"
                            >
                              <WhatsAppIcon className="w-4 h-4 text-emerald-400" />
                            </button>
                            <button
                              onClick={() => verDetalle(prospecto)}
                              className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4 text-zinc-400" />
                            </button>
                            <button
                              onClick={() => abrirEditar(prospecto)}
                              className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit className="w-4 h-4 text-zinc-400" />
                            </button>
                            <button
                              onClick={() => crearCotizacion(prospecto)}
                              className="p-1.5 hover:bg-blue-500/20 rounded-lg transition-colors"
                              title="Crear cotización"
                            >
                              <DollarSign className="w-4 h-4 text-blue-400" />
                            </button>
                            <button
                              onClick={() => crearFicha(prospecto)}
                              className="p-1.5 hover:bg-purple-500/20 rounded-lg transition-colors"
                              title="Crear ficha"
                            >
                              <FileText className="w-4 h-4 text-purple-400" />
                            </button>
                            {canManage && (
                              <button
                                onClick={() => handleEliminar(prospecto)}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-sm text-zinc-400">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-zinc-800 rounded-lg text-sm disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 bg-zinc-800 rounded-lg text-sm disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal Crear/Editar Prospecto */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="text-lg font-semibold">
                {editando ? 'Editar Prospecto' : 'Nuevo Prospecto'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Info de creador (solo para nuevo prospecto) */}
              {!editando && (
                <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3 border border-zinc-700/50">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Creado por</p>
                    <p className="text-sm font-medium text-white">{user?.nombre || user?.username || user?.email}</p>
                  </div>
                </div>
              )}

              {/* Fila 1: Asesor Responsable y Fecha evento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">
                    {editando ? 'Asesor responsable' : 'Atención inicial realizada por'} *
                  </label>
                  
                  {/* Si es Admin/Gerencia o está editando: mostrar selector */}
                  {(isAdmin?.() || isGerencia?.() || editando) ? (
                    <select
                      value={formData.asesor_inicial_id}
                      onChange={(e) => setFormData({...formData, asesor_inicial_id: e.target.value})}
                      className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                      data-testid="asesor-selector"
                    >
                      <option value="">Seleccionar asesor...</option>
                      {opciones.asesores.map(a => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                  ) : (
                    /* Si es vendedor normal creando: mostrar su nombre (no editable) */
                    <div className="w-full px-3 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white flex items-center justify-between">
                      <span>
                        {vendedorActual 
                          ? vendedorActual.nombre 
                          : (user?.nombre || user?.username || 'Usuario actual')
                        }
                      </span>
                      <span className="text-xs text-zinc-500">(automático)</span>
                    </div>
                  )}
                  
                  {/* Nota para vendedores */}
                  {!editando && !isAdmin?.() && !isGerencia?.() && !vendedorActual && (
                    <p className="text-xs text-amber-400 mt-1">
                      No estás registrado como vendedor autorizado. El prospecto quedará asociado a tu usuario.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Fecha de evento</label>
                  <input
                    type="date"
                    value={formData.fecha_evento}
                    onChange={(e) => setFormData({...formData, fecha_evento: e.target.value})}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              {/* Fila 2: Nombre */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Nombre completo del prospecto"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                />
              </div>

              {/* Fila 3: Canal y Teléfono */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">De dónde escribe *</label>
                  <select
                    value={formData.canal_origen}
                    onChange={(e) => setFormData({...formData, canal_origen: e.target.value})}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  >
                    {opciones.canales_origen.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Teléfono de contacto *</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="3001234567"
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              {/* Fila 4: Correo */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Correo electrónico</label>
                <input
                  type="email"
                  value={formData.correo}
                  onChange={(e) => setFormData({...formData, correo: e.target.value})}
                  placeholder="correo@ejemplo.com"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                />
              </div>

              {/* Fila 5: Prenda y Campaña */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Qué prenda estás buscando</label>
                  <select
                    value={formData.prenda_buscada}
                    onChange={(e) => setFormData({...formData, prenda_buscada: e.target.value})}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  >
                    {opciones.prendas_buscadas.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  {formData.prenda_buscada === 'Otro' && (
                    <input
                      type="text"
                      value={formData.prenda_buscada_otro}
                      onChange={(e) => setFormData({...formData, prenda_buscada_otro: e.target.value})}
                      placeholder="Especificar prenda..."
                      className="w-full mt-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                    />
                  )}
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Campaña publicidad</label>
                  <select
                    value={formData.campana_publicidad}
                    onChange={(e) => setFormData({...formData, campana_publicidad: e.target.value})}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  >
                    {opciones.campanas_publicidad.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {formData.campana_publicidad === 'Otro' && (
                    <input
                      type="text"
                      value={formData.campana_otro}
                      onChange={(e) => setFormData({...formData, campana_otro: e.target.value})}
                      placeholder="Especificar campaña..."
                      className="w-full mt-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                    />
                  )}
                </div>
              </div>

              {/* Fila 6: Estado */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Estado del seguimiento</label>
                <select
                  value={formData.estado_seguimiento}
                  onChange={(e) => setFormData({...formData, estado_seguimiento: e.target.value})}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                >
                  {opciones.estados_seguimiento.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>

              {/* Fila 7: Comentarios */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Comentarios</label>
                <textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData({...formData, comentarios: e.target.value})}
                  placeholder="Notas sobre la conversación, gustos, presupuesto, urgencia, etc..."
                  rows={4}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none resize-none"
                />
              </div>

              {/* Fila 8: Próxima fecha contacto */}
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Próxima fecha de contacto</label>
                <input
                  type="date"
                  value={formData.proxima_fecha_contacto}
                  onChange={(e) => setFormData({...formData, proxima_fecha_contacto: e.target.value})}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>

            <div className="p-5 border-t border-zinc-800 flex gap-3">
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="flex-1 px-4 py-3 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleGuardar(false)}
                disabled={guardando}
                className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50"
              >
                {guardando ? 'Guardando...' : editando ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Duplicado Detectado */}
      {showDuplicadoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-lg w-full">
            <div className="p-5 border-b border-amber-500/30 bg-amber-500/10">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <div>
                  <h3 className="text-lg font-semibold text-amber-500">Cliente ya existe</h3>
                  <p className="text-sm text-amber-200/70">
                    Se encontró un registro similar en el sistema
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-400">
                ¿Deseas abrir el registro existente y agregar un nuevo contacto al historial?
              </p>
              
              {duplicados.map((dup) => (
                <div key={dup.id} className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-white">{dup.nombre}</h4>
                      <p className="text-sm text-zinc-400">{dup.telefono}</p>
                      {dup.correo && <p className="text-sm text-zinc-500">{dup.correo}</p>}
                      <p className="text-xs text-zinc-500 mt-1">
                        {dup.numero_contactos || 1} contacto(s) • Estado: {dup.estado_seguimiento}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => irAProspectoExistente(dup)}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      Abrir existente
                    </button>
                    <button
                      onClick={() => agregarContactoAExistente(dup)}
                      className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                    >
                      Agregar al historial
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-5 border-t border-zinc-800 flex gap-3">
              <button
                onClick={() => setShowDuplicadoModal(false)}
                className="flex-1 px-4 py-2 border border-zinc-700 rounded-lg hover:bg-zinc-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleGuardar(true)}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Crear nuevo de todos modos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle / Historial */}
      {showDetalleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <h3 className="text-lg font-semibold">Detalle del Prospecto</h3>
              <button onClick={() => setShowDetalleModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingDetalle ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#D4AF37]" />
              </div>
            ) : prospectoDetalle && (
              <div className="p-5 space-y-6">
                {/* Info principal */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-2xl font-bold text-white">{prospectoDetalle.nombre}</h4>
                    <p className="text-zinc-400">{prospectoDetalle.telefono}</p>
                    {prospectoDetalle.correo && <p className="text-zinc-500 text-sm">{prospectoDetalle.correo}</p>}
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      ESTADO_COLORS[prospectoDetalle.estado_seguimiento] || 'bg-zinc-100 text-zinc-800'
                    }`}>
                      {prospectoDetalle.estado_seguimiento}
                    </span>
                    <p className="text-sm text-zinc-500 mt-2">
                      {prospectoDetalle.numero_contactos} contacto(s)
                    </p>
                  </div>
                </div>

                {/* Datos del registro */}
                <div className="bg-zinc-800 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500">Canal</p>
                    <p className="text-white">{prospectoDetalle.canal_origen}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Prenda</p>
                    <p className="text-white">
                      {prospectoDetalle.prenda_buscada === 'Otro' 
                        ? prospectoDetalle.prenda_buscada_otro 
                        : prospectoDetalle.prenda_buscada}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Campaña</p>
                    <p className="text-white">{prospectoDetalle.campana_publicidad}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Fecha evento</p>
                    <p className="text-white">{formatDate(prospectoDetalle.fecha_evento)}</p>
                  </div>
                </div>

                {/* Comentarios */}
                {prospectoDetalle.comentarios && (
                  <div className="bg-zinc-800 rounded-lg p-4">
                    <p className="text-zinc-500 text-sm mb-2">Comentarios</p>
                    <p className="text-zinc-300 whitespace-pre-wrap">{prospectoDetalle.comentarios}</p>
                  </div>
                )}

                {/* Acciones rápidas */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => contactarWhatsApp(prospectoDetalle)}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    Contactar WhatsApp
                  </button>
                  <button
                    onClick={() => { setShowContactoModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar contacto
                  </button>
                  <button
                    onClick={() => crearCotizacion(prospectoDetalle)}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    <DollarSign className="w-4 h-4" />
                    Crear cotización
                  </button>
                  <button
                    onClick={() => crearFicha(prospectoDetalle)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <FileText className="w-4 h-4" />
                    Crear ficha
                  </button>
                </div>

                {/* Historial de contactos */}
                <div>
                  <h5 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    Historial de Contactos ({prospectoDetalle.historial_contactos?.length || 0})
                  </h5>
                  
                  <div className="space-y-3">
                    {prospectoDetalle.historial_contactos?.slice().reverse().map((contacto, idx) => (
                      <div key={contacto.id || idx} className="bg-zinc-800 rounded-lg p-4 border-l-4 border-[#D4AF37]">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{contacto.canal}</span>
                              <span className="text-xs text-zinc-500">•</span>
                              <span className="text-xs text-zinc-500">{contacto.asesor_nombre}</span>
                            </div>
                            <p className="text-sm text-zinc-400 mt-1">{contacto.comentario}</p>
                            {contacto.estado_despues && (
                              <p className="text-xs text-emerald-400 mt-1">
                                → Estado: {contacto.estado_despues}
                              </p>
                            )}
                            {contacto.proxima_accion && (
                              <p className="text-xs text-amber-400 mt-1">
                                Próxima acción: {contacto.proxima_accion}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-zinc-500 whitespace-nowrap">
                            {formatDateTime(contacto.fecha)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cotizaciones y fichas asociadas */}
                {(prospectoDetalle.cotizaciones?.length > 0 || prospectoDetalle.fichas?.length > 0) && (
                  <div className="grid grid-cols-2 gap-4">
                    {prospectoDetalle.cotizaciones?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold mb-2 text-zinc-400">Cotizaciones asociadas</h5>
                        {prospectoDetalle.cotizaciones.map(cot => (
                          <div key={cot.id} className="bg-zinc-800 rounded-lg p-3 mb-2">
                            <p className="text-sm text-white">#{cot.numero}</p>
                            <p className="text-xs text-zinc-400">{cot.estado} • ${cot.total?.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {prospectoDetalle.fichas?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold mb-2 text-zinc-400">Fichas asociadas</h5>
                        {prospectoDetalle.fichas.map(ficha => (
                          <div key={ficha.id} className="bg-zinc-800 rounded-lg p-3 mb-2">
                            <p className="text-sm text-white">{ficha.nombre}</p>
                            <p className="text-xs text-zinc-400">{ficha.estado}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Agregar Contacto */}
      {showContactoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-md w-full">
            <div className="p-5 border-b border-zinc-800">
              <h3 className="text-lg font-semibold">Agregar Contacto al Historial</h3>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Canal de contacto</label>
                <select
                  value={nuevoContacto.canal}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, canal: e.target.value})}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                >
                  {opciones.canales_origen.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Comentario / Resumen</label>
                <textarea
                  value={nuevoContacto.comentario}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, comentario: e.target.value})}
                  placeholder="¿Qué se habló? ¿Qué preguntó el cliente?"
                  rows={3}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none resize-none"
                />
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Nuevo estado (opcional)</label>
                <select
                  value={nuevoContacto.estado_despues}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, estado_despues: e.target.value})}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                >
                  <option value="">Sin cambio</option>
                  {opciones.estados_seguimiento.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Próxima acción (opcional)</label>
                <input
                  type="text"
                  value={nuevoContacto.proxima_accion}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, proxima_accion: e.target.value})}
                  placeholder="Ej: Llamar el lunes, Enviar cotización..."
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
            
            <div className="p-5 border-t border-zinc-800 flex gap-3">
              <button
                onClick={() => setShowContactoModal(false)}
                className="flex-1 px-4 py-2 border border-zinc-700 rounded-lg hover:bg-zinc-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleAgregarContacto}
                className="flex-1 px-4 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#C4A030]"
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODAL IMPORTACIÓN EXCEL ==================== */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Importar Clientes desde Excel</h3>
                  <p className="text-sm text-zinc-400">
                    {importStep === 'upload' && 'Sube tu archivo Excel con los datos de clientes'}
                    {importStep === 'preview' && 'Revisa los datos antes de importar'}
                    {importStep === 'importing' && 'Importando datos...'}
                    {importStep === 'done' && 'Importación completada'}
                  </p>
                </div>
              </div>
              <button onClick={cerrarImportModal} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido según el paso */}
            <div className="flex-1 overflow-y-auto p-5">
              
              {/* PASO 1: Subir archivo */}
              {importStep === 'upload' && (
                <div className="space-y-6">
                  {/* Zona de upload */}
                  <div 
                    className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    {importLoading ? (
                      <div className="flex flex-col items-center">
                        <RefreshCw className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
                        <p className="text-zinc-400">Procesando archivo...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="w-12 h-12 text-zinc-500 mb-4" />
                        <p className="text-white font-medium mb-2">Arrastra tu archivo Excel aquí</p>
                        <p className="text-zinc-400 text-sm">o haz clic para seleccionar</p>
                        <p className="text-zinc-500 text-xs mt-2">Formatos aceptados: .xlsx, .xls</p>
                      </div>
                    )}
                  </div>

                  {/* Descargar plantilla */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">¿No tienes el formato correcto?</p>
                        <p className="text-sm text-zinc-400">Descarga nuestra plantilla con las columnas requeridas</p>
                      </div>
                      <button
                        onClick={descargarPlantilla}
                        className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-sm transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Descargar plantilla
                      </button>
                    </div>
                  </div>

                  {/* Columnas requeridas */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <p className="font-medium text-white mb-3">Columnas del Excel:</p>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      {[
                        'Quién realiza atención inicial',
                        'Fecha de evento',
                        'Nombre',
                        'De dónde escribe',
                        'Teléfono de contacto',
                        'Correo electrónico',
                        'Qué prenda estás buscando',
                        'Campaña publicidad',
                        'Comentarios'
                      ].map((col) => (
                        <div key={col} className="flex items-center gap-2 text-zinc-400">
                          <Check className="w-3 h-3 text-emerald-500" />
                          {col}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* PASO 2: Vista previa */}
              {importStep === 'preview' && importPreview && (
                <div className="space-y-6">
                  {/* Estadísticas */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">{importPreview.estadisticas.total_registros}</p>
                      <p className="text-xs text-zinc-400">Total</p>
                    </div>
                    <div className="bg-emerald-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{importPreview.estadisticas.registros_validos}</p>
                      <p className="text-xs text-emerald-300">Válidos</p>
                    </div>
                    <div className="bg-red-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-400">{importPreview.estadisticas.registros_con_errores}</p>
                      <p className="text-xs text-red-300">Con errores</p>
                    </div>
                    <div className="bg-amber-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-400">{importPreview.estadisticas.posibles_duplicados}</p>
                      <p className="text-xs text-amber-300">Duplicados</p>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">{importPreview.estadisticas.listos_para_importar}</p>
                      <p className="text-xs text-blue-300">Listos</p>
                    </div>
                  </div>

                  {/* Configuración de importación */}
                  <div className="bg-zinc-800 rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-white">Configuración de importación</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Estado inicial para nuevos</label>
                        <select
                          value={importEstadoInicial}
                          onChange={(e) => setImportEstadoInicial(e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                        >
                          {opciones.estados_seguimiento.map(e => (
                            <option key={e} value={e}>{e}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Acción para duplicados</label>
                        <select
                          value={importAccionDuplicados}
                          onChange={(e) => setImportAccionDuplicados(e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                        >
                          <option value="agregar_historial">Agregar al historial</option>
                          <option value="actualizar">Actualizar datos</option>
                          <option value="ignorar">Ignorar</option>
                          <option value="crear_nuevo">Crear como nuevo</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Tabla de preview */}
                  <div className="bg-zinc-800 rounded-lg overflow-hidden">
                    <div className="p-3 border-b border-zinc-700">
                      <h4 className="font-medium text-white">Vista previa de datos (primeros 100)</h4>
                    </div>
                    <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-700/50 sticky top-0">
                          <tr className="text-left text-xs text-zinc-400">
                            <th className="px-3 py-2">Estado</th>
                            <th className="px-3 py-2">Fila</th>
                            <th className="px-3 py-2">Nombre</th>
                            <th className="px-3 py-2">Teléfono</th>
                            <th className="px-3 py-2">Correo</th>
                            <th className="px-3 py-2">Canal</th>
                            <th className="px-3 py-2">Notas</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-700">
                          {importPreview.registros.map((reg, idx) => (
                            <tr key={`row-${reg.fila_excel || idx}`} className={`${
                              reg.errores?.length > 0 ? 'bg-red-500/10' :
                              reg.es_duplicado ? 'bg-amber-500/10' : ''
                            }`}>
                              <td className="px-3 py-2">
                                {reg.errores?.length > 0 ? (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                ) : reg.es_duplicado ? (
                                  <AlertCircle className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                )}
                              </td>
                              <td className="px-3 py-2 text-zinc-500">{reg.fila_excel}</td>
                              <td className="px-3 py-2 text-white">{reg.datos_procesados?.nombre || '-'}</td>
                              <td className="px-3 py-2 text-zinc-300">{reg.datos_procesados?.telefono || '-'}</td>
                              <td className="px-3 py-2 text-zinc-400 text-xs">{reg.datos_procesados?.correo || '-'}</td>
                              <td className="px-3 py-2 text-zinc-400 text-xs">{reg.datos_procesados?.canal_origen || '-'}</td>
                              <td className="px-3 py-2">
                                {reg.errores?.length > 0 && (
                                  <span className="text-xs text-red-400">{reg.errores.join(', ')}</span>
                                )}
                                {reg.es_duplicado && (
                                  <span className="text-xs text-amber-400">
                                    Duplicado: {reg.duplicado_existente?.nombre}
                                  </span>
                                )}
                                {reg.advertencias?.length > 0 && (
                                  <span className="text-xs text-zinc-500">{reg.advertencias.join(', ')}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* PASO 3: Importando */}
              {importStep === 'importing' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="w-16 h-16 text-emerald-500 animate-spin mb-6" />
                  <h4 className="text-xl font-medium text-white mb-2">Importando datos...</h4>
                  <p className="text-zinc-400">Por favor espera mientras procesamos los registros</p>
                </div>
              )}

              {/* PASO 4: Completado */}
              {importStep === 'done' && importResultado && (
                <div className="space-y-6">
                  <div className="flex flex-col items-center py-6">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
                    <h4 className="text-xl font-medium text-white mb-2">¡Importación completada!</h4>
                  </div>

                  {/* Resumen */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">{importResultado.resumen.total_procesados}</p>
                      <p className="text-xs text-zinc-400">Procesados</p>
                    </div>
                    <div className="bg-emerald-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{importResultado.resumen.clientes_creados}</p>
                      <p className="text-xs text-emerald-300">Creados</p>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">{importResultado.resumen.clientes_actualizados}</p>
                      <p className="text-xs text-blue-300">Actualizados</p>
                    </div>
                    <div className="bg-purple-500/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-400">{importResultado.resumen.contactos_agregados_historial}</p>
                      <p className="text-xs text-purple-300">Al historial</p>
                    </div>
                    <div className="bg-zinc-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-zinc-300">{importResultado.resumen.registros_ignorados}</p>
                      <p className="text-xs text-zinc-400">Ignorados</p>
                    </div>
                  </div>

                  {/* Errores si hay */}
                  {importResultado.errores?.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h5 className="font-medium text-red-400 mb-2">
                        {importResultado.errores.length} registro(s) con errores:
                      </h5>
                      <ul className="text-sm text-red-300 space-y-1">
                        {importResultado.errores.slice(0, 10).map((err) => (
                          <li key={`error-fila-${err.fila}-${err.error?.substring(0, 20)}`}>Fila {err.fila}: {err.error}</li>
                        ))}
                        {importResultado.errores.length > 10 && (
                          <li>... y {importResultado.errores.length - 10} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer con botones */}
            <div className="p-5 border-t border-zinc-800 flex gap-3">
              {importStep === 'upload' && (
                <button
                  onClick={cerrarImportModal}
                  className="flex-1 px-4 py-3 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  Cancelar
                </button>
              )}
              
              {importStep === 'preview' && (
                <>
                  <button
                    onClick={() => { setImportStep('upload'); setImportPreview(null); setImportFile(null); }}
                    className="px-4 py-3 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    onClick={ejecutarImportacion}
                    disabled={importLoading || importPreview?.estadisticas?.registros_validos === 0}
                    className="flex-1 px-4 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Confirmar Importación ({importPreview?.estadisticas?.registros_validos || 0} registros)
                  </button>
                </>
              )}
              
              {importStep === 'done' && (
                <button
                  onClick={cerrarImportModal}
                  className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#C4A030] transition-colors"
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMProspectos;
